import { whyUnplaced } from '@servwell/shared';
import type { MapPin } from '../map/DispatchMap';
import { pinToneFor } from '../map/visitPinTone';

/**
 * The day's visits as map pins — and, just as importantly, the ones that could not be drawn
 * (114 part 4).
 *
 * ⭐ Pure and separate from the page for the same reason `./timeline` is: `apps/web` runs
 * its tests in node, so anything that decides WHICH stops appear has to live where a test
 * can hold it to account. What is left in the page is wiring.
 */
export interface PinSource {
  /** The VISIT id. It is what the panel, the card and the pin all key on. */
  id: string;
  status: string;
  title: string;
  customer: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  geocodeStatus: string | null;
  /** Minutes since midnight at the shop — the order of the day, resolved on the org clock. */
  startMinute: number;
}

export interface UnplacedStop {
  id: string;
  /** The same number its pin would have carried, so the two lists line up. */
  order: number;
  label: string;
  why: string;
}

/**
 * Turn a day's rows into pins and the un-drawable remainder.
 *
 * ⚠️ **The numbering runs over ALL the day's stops, not over the drawable ones.** A pin's
 * number is its place in the shop's day, which is a property of the visit; renumbering
 * around a stop the geocoder could not place would mean fixing one address silently
 * renumbers every pin after it. So the map can read "1, 2, 4" — and the gap is honest,
 * because the pane names stop 3 underneath with the reason it is missing.
 *
 * ⚠️ **One pass, one partition** — never two filters over the same array. `fleet.ts` records
 * why: with two filters, changing the coordinate test in one of them puts a row in NEITHER
 * list, and a call that is in no list reads on screen as a call that is not happening.
 *
 * ⚠️ **It sorts.** The server already returns the board ordered by start time, but a
 * number that is only correct because of somebody else's `ORDER BY` is a number one query
 * plan away from being wrong. The tie-break is the id, so two 9am visits never swap places
 * between renders.
 */
export function dayPins(rows: PinSource[]): { pins: MapPin[]; unplaced: UnplacedStop[] } {
  const ordered = [...rows].sort(
    (a, b) => a.startMinute - b.startMinute || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  const pins: MapPin[] = [];
  const unplaced: UnplacedStop[] = [];
  ordered.forEach((r, i) => {
    const order = i + 1;
    if (r.latitude !== null && r.longitude !== null) {
      pins.push({
        id: r.id,
        latitude: r.latitude,
        longitude: r.longitude,
        order,
        title: r.title,
        body: [r.customer, r.address].filter(Boolean).join(' · '),
        tone: pinToneFor(r.status),
      });
    } else {
      unplaced.push({
        id: r.id,
        order,
        label: [r.customer, r.title].filter(Boolean).join(' · '),
        // The shop already reads these three sentences on `/fleet`; `whyUnplaced` lives in
        // shared so the second screen cannot invent a fourth wording for the same fact.
        why: whyUnplaced(r.geocodeStatus),
      });
    }
  });
  return { pins, unplaced };
}
